import { useState, useEffect } from 'react';
import { getTierConfig, saveTierConfig } from '../../utils/accountStorage';
import { TIER_DEFAULTS, TIER_NAMES, TIER_ORDER } from '../../config/tierDefaults';

const EXPORT_FORMATS = ['csv', 'xlsx', 'json', 'pdf', 'doc', 'taxreport'];

function NumField({ field, label, hint, current, onUpdate }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
      <div>
        <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{hint}</div>}
      </div>
      <input
        type="number"
        value={current[field]}
        onChange={e => onUpdate(field, parseInt(e.target.value) === -1 ? -1 : Math.max(-1, parseInt(e.target.value) || 0))}
        style={{ width: 80, padding: '7px 8px', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 13, textAlign: 'right', outline: 'none', fontFamily: 'inherit' }}
      />
    </div>
  );
}

function BoolField({ field, label, current, onUpdate }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{label}</span>
      <input
        type="checkbox"
        checked={!!current[field]}
        onChange={e => onUpdate(field, e.target.checked)}
        style={{ width: 16, height: 16, cursor: 'pointer' }}
      />
    </div>
  );
}

function AdminTierEditor() {
  const [tierData,   setTierData]   = useState(null);
  const [activeTier, setActiveTier] = useState('basic');
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    getTierConfig().then(config => {
      const merged = {};
      for (const t of TIER_ORDER) {
        merged[t] = { ...TIER_DEFAULTS[t], ...(config?.[t] || {}) };
      }
      setTierData(merged);
    });
  }, []);

  const update = (field, value) =>
    setTierData(prev => ({ ...prev, [activeTier]: { ...prev[activeTier], [field]: value } }));

  const toggleFormat = (fmt) => {
    const current = tierData[activeTier].exportFormats || [];
    const next = current.includes(fmt) ? current.filter(f => f !== fmt) : [...current, fmt];
    update('exportFormats', next);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveTierConfig(tierData);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('saveTierConfig failed:', err);
      setError('Save failed — ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!tierData) {
    return <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 13 }}>Loading…</div>;
  }

  const current = tierData[activeTier];
  const fieldProps = { current, onUpdate: update };

  return (
    <div>
      {/* Tier tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TIER_ORDER.map(t => (
          <button
            key={t}
            onClick={() => setActiveTier(t)}
            style={{
              flex: 1, padding: '9px', borderRadius: 8,
              border: activeTier === t ? '2px solid #0A7C8A' : '1.5px solid #E5E7EB',
              background: activeTier === t ? '#0A7C8A18' : '#fff',
              color: activeTier === t ? '#0A7C8A' : '#6B7280',
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            {TIER_NAMES[t]}
          </button>
        ))}
      </div>

      <NumField field="scansPerMonth"        label="Scans / month"         hint="-1 = unlimited"        {...fieldProps} />
      <NumField field="maxReports"           label="Max reports"           hint="-1 = unlimited"        {...fieldProps} />
      <NumField field="maxExpensesPerReport" label="Max expenses / report" hint="-1 = unlimited"        {...fieldProps} />
      <NumField field="maxCollaborators"     label="Max collaborators"     hint="0 = sharing disabled"  {...fieldProps} />
      <BoolField field="imageStorage"        label="Receipt image storage"      {...fieldProps} />
      <BoolField field="canCombineReports"   label="Combine reports on export"  {...fieldProps} />
      <BoolField field="canShare"            label="Report sharing"             {...fieldProps} />
      <BoolField field="canShareEdit"        label="Edit access sharing"        {...fieldProps} />
      <BoolField field="customCategories"    label="Custom categories"          {...fieldProps} />
      <BoolField field="taxReport"           label="Tax report export"          {...fieldProps} />
      <BoolField field="auditTrail"          label="Audit trail in exports"     {...fieldProps} />
      <BoolField field="groupBy"             label="Group by"                   {...fieldProps} />

      {/* AI Bookkeeper */}
      <div style={{ margin: '18px 0 4px', fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase' }}>
        AI Bookkeeper
      </div>
      <BoolField field="aiBookkeeper"       label="AI Bookkeeper access"          {...fieldProps} />
      <NumField  field="aiContextLen"       label="Context limit (tokens)"        hint="Tokens before auto-condensation" {...fieldProps} />
      <NumField  field="aiQueriesPerWeek"   label="Queries per week"              hint="-1 = unlimited" {...fieldProps} />
      <NumField  field="aiTokensInPerWeek"  label="Prompt tokens per week"        hint="-1 = unlimited" {...fieldProps} />
      <NumField  field="aiTokensOutPerWeek"  label="Completion tokens per week"  hint="-1 = unlimited" {...fieldProps} />
      <NumField  field="aiToolCallsPerWeek"  label="Tool calls per week"         hint="-1 = unlimited" {...fieldProps} />
      <NumField  field="aiToolCallsPerMonth" label="Tool calls per month"        hint="-1 = unlimited" {...fieldProps} />

      {/* Export formats */}
      <div style={{ padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ fontSize: 13, color: '#374151', fontWeight: 600, marginBottom: 8 }}>Export formats</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {EXPORT_FORMATS.map(fmt => {
            const on = (current.exportFormats || []).includes(fmt);
            return (
              <label
                key={fmt}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px',
                  borderRadius: 6, cursor: 'pointer',
                  background: on ? '#0A7C8A18' : '#F9FAFB',
                  border: `1.5px solid ${on ? '#0A7C8A' : '#E5E7EB'}`,
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleFormat(fmt)}
                  style={{ margin: 0 }}
                />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{fmt}</span>
              </label>
            );
          })}
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#FEF2F2', borderRadius: 8, fontSize: 12, color: '#DC2626' }}>
          {error}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          marginTop: 24, width: '100%', padding: '13px', borderRadius: 10,
          border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
          background: saved ? '#10B981' : 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
          color: '#fff', fontWeight: 700, fontSize: 14, transition: 'background 0.2s',
        }}
      >
        {saving ? 'Saving…' : saved ? 'Saved ✓' : `Save ${TIER_NAMES[activeTier]} Tier`}
      </button>

      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, textAlign: 'center' }}>
        Changes apply to all users on this tier immediately.
      </div>
    </div>
  );
}

export default AdminTierEditor;
